import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  Activity, 
  AlertTriangle,
  Zap,
  RefreshCw
} from 'lucide-react'
import { api } from '@/lib/api'

interface TimeRange {
  label: string
  value: string
  days: number
}

interface TrendDataPoint {
  timestamp: string
  avg_response_time: number
  request_count: number
  error_rate: number
  cpu_usage: number
  memory_usage: number
}

interface PerformanceTrendsProps {
  timeRange: TimeRange
}

export function PerformanceTrends({ timeRange }: PerformanceTrendsProps) {
  const [trendData, setTrendData] = useState<TrendDataPoint[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedMetric, setSelectedMetric] = useState<'response_time' | 'requests' | 'errors' | 'cpu'>('response_time')

  useEffect(() => {
    fetchTrendData()
  }, [timeRange])

  const fetchTrendData = async () => {
    try {
      setLoading(true)
      // Use backend usage trends endpoint (aggregated across user's servers)
      const res = await api.get<any>('/monitoring/usage-trends', { time_range: timeRange.value })
      const list = Array.isArray(res?.usage_trends) ? res.usage_trends : []

      const dataPoints: TrendDataPoint[] = list.map((it: any) => ({
        timestamp: typeof it.timestamp === 'string' ? it.timestamp : new Date().toISOString(),
        avg_response_time: Number(it.avg_response_time ?? 0),
        request_count: Number(it.request_count ?? 0),
        error_rate: Number(it.error_rate ?? 0),
        // CPU/memory are not time-series in this endpoint; default 0 for now
        cpu_usage: 0,
        memory_usage: 0,
      }))

      setTrendData(dataPoints)
    } catch (error) {
      console.error('Failed to fetch trend data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getMetricConfig = () => {
    switch (selectedMetric) {
      case 'response_time':
        return {
          label: 'Response Time',
          unit: 'ms',
          color: '#F59E0B',
          data: trendData.map(d => d.avg_response_time),
          icon: Clock
        }
      case 'requests':
        return {
          label: 'Requests',
          unit: '',
          color: '#3B82F6',
          data: trendData.map(d => d.request_count),
          icon: Activity
        }
      case 'errors':
        return {
          label: 'Error Rate',
          unit: '%',
          color: '#EF4444',
          data: trendData.map(d => d.error_rate),
          icon: AlertTriangle
        }
      case 'cpu':
        return {
          label: 'CPU Usage',
          unit: '%',
          color: '#8B5CF6',
          data: trendData.map(d => d.cpu_usage),
          icon: Zap
        }
      default:
        return {
          label: 'Response Time',
          unit: 'ms',
          color: '#F59E0B',
          data: trendData.map(d => d.avg_response_time),
          icon: Clock
        }
    }
  }

  const calculateTrend = (data: number[]) => {
    if (data.length < 2) return 0
    
    const recent = data.slice(-Math.ceil(data.length / 4)) // Last quarter
    const older = data.slice(0, Math.ceil(data.length / 4)) // First quarter
    
    const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length
    const olderAvg = older.reduce((a, b) => a + b, 0) / older.length
    
    return ((recentAvg - olderAvg) / olderAvg) * 100
  }

  const renderTrendChart = (data: number[], color: string) => {
    if (data.length === 0) return null

    const width = 400
    const height = 200
    const padding = 20

    const maxValue = Math.max(...data)
    const minValue = Math.min(...data)
    const range = maxValue - minValue || 1

    const points = data.map((value, index) => {
      const x = padding + (index / (data.length - 1)) * (width - 2 * padding)
      const y = height - padding - ((value - minValue) / range) * (height - 2 * padding)
      return `${x},${y}`
    }).join(' ')

    const pathData = `M ${points.replace(/,/g, ' L ')}`

    // Create area fill path
    const firstPoint = points.split(' ')[0]
    const lastPoint = points.split(' ').slice(-1)[0]
    const lastX = lastPoint.split(',')[0]
    const firstX = firstPoint.split(',')[0]
    const areaPath = `${pathData} L ${lastX},${height - padding} L ${firstX},${height - padding} Z`

    return (
      <div className="w-full flex justify-center">
        <svg width={width} height={height} className="border rounded-lg bg-gray-50">
          {/* Grid lines */}
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e5e7eb" strokeWidth="1"/>
            </pattern>
            <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.3 }} />
              <stop offset="100%" style={{ stopColor: color, stopOpacity: 0.1 }} />
            </linearGradient>
          </defs>
          
          <rect width="100%" height="100%" fill="url(#grid)" />
          
          {/* Area fill */}
          <path
            d={areaPath}
            fill="url(#areaGradient)"
          />
          
          {/* Trend line */}
          <path
            d={pathData}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          
          {/* Data points */}
          {data.map((value, index) => {
            const x = padding + (index / (data.length - 1)) * (width - 2 * padding)
            const y = height - padding - ((value - minValue) / range) * (height - 2 * padding)
            return (
              <circle
                key={index}
                cx={x}
                cy={y}
                r="3"
                fill={color}
                className="hover:r-4 transition-all"
              />
            )
          })}
          
          {/* Y-axis labels */}
          <text x="5" y="25" className="text-xs fill-gray-500">{Math.round(maxValue)}</text>
          <text x="5" y={height - 10} className="text-xs fill-gray-500">{Math.round(minValue)}</text>
        </svg>
      </div>
    )
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Performance Trends</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-500 mr-2" />
            <span className="text-muted-foreground">Loading trend data...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  const metricConfig = getMetricConfig()
  const trend = calculateTrend(metricConfig.data)
  const currentValue = metricConfig.data[metricConfig.data.length - 1] || 0
  const Icon = metricConfig.icon

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Chart */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5" />
            <span>Performance Trends</span>
          </CardTitle>
          <CardDescription>Historical performance data for {timeRange.label.toLowerCase()}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Metric Selection */}
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'response_time', label: 'Response Time', icon: Clock },
                { key: 'requests', label: 'Requests', icon: Activity },
                { key: 'errors', label: 'Error Rate', icon: AlertTriangle },
                { key: 'cpu', label: 'CPU Usage', icon: Zap }
              ].map(({ key, label, icon: MetricIcon }) => (
                <Button
                  key={key}
                  variant={selectedMetric === key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedMetric(key as any)}
                  className="flex items-center space-x-1"
                >
                  <MetricIcon className="h-4 w-4" />
                  <span>{label}</span>
                </Button>
              ))}
            </div>

            {/* Current Value */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon className="h-6 w-6 text-blue-500" />
                <div>
                  <h3 className="text-lg font-semibold">{metricConfig.label}</h3>
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl font-bold">
                      {selectedMetric === 'requests' 
                        ? currentValue.toLocaleString() 
                        : Math.round(currentValue)}{metricConfig.unit}
                    </span>
                    <div className="flex items-center space-x-1">
                      {trend > 0 ? (
                        <TrendingUp className="h-4 w-4 text-green-500" />
                      ) : trend < 0 ? (
                        <TrendingDown className="h-4 w-4 text-red-500" />
                      ) : null}
                      <Badge variant={Math.abs(trend) > 5 ? "destructive" : "outline"}>
                        {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Chart */}
            {renderTrendChart(metricConfig.data, metricConfig.color)}

            {/* Time range indicator */}
            <div className="text-center text-sm text-muted-foreground">
              Showing {trendData.length} data points over {timeRange.label.toLowerCase()}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Summary Statistics</CardTitle>
          <CardDescription>Key metrics for {timeRange.label.toLowerCase()}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Response Time Stats */}
            <div>
              <h4 className="font-medium mb-3 flex items-center space-x-2">
                <Clock className="h-4 w-4" />
                <span>Response Time</span>
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Average</span>
                  <span className="font-medium">
                    {Math.round(trendData.reduce((sum, d) => sum + d.avg_response_time, 0) / trendData.length)}ms
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Best</span>
                  <span className="font-medium text-green-600">
                    {Math.round(Math.min(...trendData.map(d => d.avg_response_time)))}ms
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Worst</span>
                  <span className="font-medium text-red-600">
                    {Math.round(Math.max(...trendData.map(d => d.avg_response_time)))}ms
                  </span>
                </div>
              </div>
            </div>

            {/* Error Rate Stats */}
            <div>
              <h4 className="font-medium mb-3 flex items-center space-x-2">
                <AlertTriangle className="h-4 w-4" />
                <span>Error Rate</span>
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Average</span>
                  <span className="font-medium">
                    {(trendData.reduce((sum, d) => sum + d.error_rate, 0) / trendData.length).toFixed(2)}%
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Peak</span>
                  <span className="font-medium text-red-600">
                    {Math.max(...trendData.map(d => d.error_rate)).toFixed(2)}%
                  </span>
                </div>
              </div>
            </div>

            {/* Resource Usage */}
            <div>
              <h4 className="font-medium mb-3 flex items-center space-x-2">
                <Zap className="h-4 w-4" />
                <span>Resource Usage</span>
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Avg CPU</span>
                  <span className="font-medium">
                    {Math.round(trendData.reduce((sum, d) => sum + d.cpu_usage, 0) / trendData.length)}%
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Avg Memory</span>
                  <span className="font-medium">
                    {Math.round(trendData.reduce((sum, d) => sum + d.memory_usage, 0) / trendData.length)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
} 